import React, { useEffect, useState } from "react";
import client from "@/api/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart2, TrendingUp, CheckCircle, XCircle, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#006080", "#059669", "#DC2626", "#D97706", "#7C3AED"];

export default function AnalyticsDashboardPage() {
  const [overview, setOverview] = useState(null);
  const [timeSeries, setTimeSeries] = useState([]);
  const [topTemplates, setTopTemplates] = useState([]);
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [days]);

  const load = async () => {
    setLoading(true);
    try {
      const [ovRes, tsRes, tmRes] = await Promise.all([
        client.get("/analytics/overview"),
        client.get("/analytics/executions-over-time", { params: { days } }),
        client.get("/analytics/top-templates"),
      ]);
      setOverview(ovRes.data);
      setTimeSeries(tsRes.data.data || []);
      setTopTemplates(tmRes.data.templates || []);
    } finally { setLoading(false); }
  };

  const stat = (label, value, icon, color, sub = "") => (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{label}</span>
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${color}15` }}>
          {React.createElement(icon, { size: 16, style: { color } })}
        </div>
      </div>
      <div className="text-3xl font-black text-gray-900">{value ?? "–"}</div>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1"><BarChart2 size={18} className="text-green-600" /><h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1></div>
          <p className="text-gray-500 text-sm">Execution trends, API performance, and processing insights</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div> : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {stat("Total Executions", overview?.total_executions?.toLocaleString(), TrendingUp, "#006080")}
            {stat("Success Rate", overview?.success_rate_pct !== undefined ? `${overview.success_rate_pct}%` : "–", CheckCircle, "#059669")}
            {stat("Avg Response", overview?.avg_response_ms !== null ? `${overview.avg_response_ms}ms` : "–", Clock, "#D97706")}
            {stat("Records Processed", overview?.total_records_processed?.toLocaleString(), BarChart2, "#7C3AED", `${overview?.total_records_success?.toLocaleString()} successful`)}
          </div>

          {/* Time series chart */}
          {timeSeries.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-4">Executions Over Time</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={timeSeries} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="_id" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="completed" stackId="a" fill="#059669" name="Completed" />
                  <Bar dataKey="failed" stackId="a" fill="#DC2626" name="Failed" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Records over time */}
          {timeSeries.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-4">Records Processed</p>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={timeSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="records" stroke="#006080" strokeWidth={2} dot={false} name="Records" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-3">Top Templates by Usage</p>
                {topTemplates.length > 0 ? topTemplates.slice(0, 5).map((t, i) => (
                  <div key={t._id} className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-bold" style={{ background: COLORS[i] }}>{i + 1}</span>
                    <span className="text-sm text-gray-700 flex-1 truncate">{t.name}</span>
                    <span className="text-xs text-gray-400">{t.pipeline_steps} steps</span>
                  </div>
                )) : <p className="text-sm text-gray-400">No template data yet</p>}
              </div>
            </div>
          )}

          {timeSeries.length === 0 && (
            <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
              <BarChart2 size={40} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400">No execution data yet. Run some API executions to see analytics.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
